import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  EventEmitter,
  Output,
  signal,
  ViewChild
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { BaseModalComponent } from '../../../../core/layout/components/abstracts/base-modal.component';
import { FormControlComponent } from '../../../../shared/components/form-control/form-control.component';
import { ModalComponent } from '../../../../shared/components/modal/modal.component';
import { TranslatePipe } from '../../../../shared/pipes/trans.pipe';
import { OnlineOrderPositionKinds } from '../../core/enums/OnlineOrderPositionKinds';
import { IOnlineOrderCodeInputData } from '../../core/interfaces/IOnlineOrderCodeInputData';
import { IOnlineOrderPosition } from '../../core/interfaces/IOnlineOrderPosition';

@Component({
  selector: 'app-online-order-code-input-modal',
  standalone: true,
  imports: [
    TranslatePipe,
    ModalComponent,
    FormControlComponent,
    FormsModule,
  ],
  templateUrl: './online-order-code-input-modal.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class OnlineOrderCodeInputModalComponent extends BaseModalComponent<IOnlineOrderCodeInputData> {

  @ViewChild('codeInput') codeInput: ElementRef<HTMLInputElement>;

  @Output() codeSubmitted = new EventEmitter<{ position: IOnlineOrderPosition; code: string; }>();

  public static instance: OnlineOrderCodeInputModalComponent;

  public code: string;
  public readonly isLoading = signal(false);

  private readonly _inputData = signal<IOnlineOrderCodeInputData>(null);

  public readonly hasInputPosition = computed(() => !!this._inputData()?.position);

  public readonly isVoucherPosition = computed(
    () => this._inputData()?.position?.kind === OnlineOrderPositionKinds.Voucher
  );

  public readonly instructionReplacements = computed((): [string, string | number][] => {
    const position = this._inputData()?.position;
    const itemName = [position?.itemName, position?.itemVariant].filter(Boolean).join(' · ');

    return [
      ['index', this._inputData()?.index],
      ['total', this._inputData()?.total],
      ['itemName', itemName],
      ['amount', `${position?.amount?.toCurrency()} ${this.languageConfig.currency.short}`],
    ];
  });

  public override open(val?: IOnlineOrderCodeInputData): void {
    OnlineOrderCodeInputModalComponent.instance = this;
    this._inputData.set(val ?? null);
    this.code = null;
    this.isLoading.set(false);
    super.open(val);
    this.codeInput?.nativeElement?.focus();
  }

  public override close(): void {
    OnlineOrderCodeInputModalComponent.instance = null;
    this._inputData.set(null);
    this.isLoading.set(false);
    super.close();
  }

  public onSubmit(): void {
    if (!this.code || this.isLoading() || !this._inputData()?.position) {
      return;
    }

    this.isLoading.set(true);
    this.codeSubmitted.emit({ position: this._inputData().position, code: this.code });
  }

  public setLoading(isLoading: boolean): void {
    this.isLoading.set(isLoading);
    this.cdr.detectChanges();
  }
}
